import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  LinearProgress,
  MenuItem,
  Snackbar,
  Stack,
  Step,
  StepButton,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import AddBusinessOutlinedIcon from "@mui/icons-material/AddBusinessOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContactPhoneOutlinedIcon from "@mui/icons-material/ContactPhoneOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import SettingsSuggestOutlinedIcon from "@mui/icons-material/SettingsSuggestOutlined";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import GlassCard from "../../components/dashboard/GlassCard";
import Header from "../../components/Header";
import saasApi from "../../utils/saasApi";

const DETECTIONS = [
  { id: "shoplifting", label: "Robo / hurto" },
  { id: "people_count", label: "Personas" },
  { id: "queue", label: "Colas" },
  { id: "weapon", label: "Arma" },
  { id: "fall", label: "Caida" },
  { id: "fire", label: "Fuego" },
  { id: "smoke", label: "Humo" },
];

const emptyBranch = { name: "", address: "", city: "" };
const emptyCamera = { name: "", display_name: "", rtsp_url: "", branch_id: "", zone_name: "" };
const emptyContact = {
  name: "",
  channel_type: "whatsapp",
  destination: "",
  start_time: "08:00",
  end_time: "22:00",
  timezone: "America/Argentina/Buenos_Aires",
  is_active: true,
};

const stepDefinitions = [
  { id: "company_branch", label: "Empresa y sucursal", icon: <AddBusinessOutlinedIcon /> },
  { id: "cameras", label: "Camaras", icon: <VideocamOutlinedIcon /> },
  { id: "connection", label: "Conexion real", icon: <PlayCircleOutlineIcon /> },
  { id: "detections", label: "Detecciones", icon: <SettingsSuggestOutlinedIcon /> },
  { id: "ai_schedule", label: "Horarios IA", icon: <TimerOutlinedIcon /> },
  { id: "contacts", label: "Contactos", icon: <ContactPhoneOutlinedIcon /> },
  { id: "final_test", label: "Prueba final", icon: <CheckCircleOutlineIcon /> },
];

const statusColor = (done) => (done ? "success" : "warning");

const Onboarding = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [bootstrap, setBootstrap] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState("");
  const [branches, setBranches] = useState([]);
  const [cameras, setCameras] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [aiInstances, setAiInstances] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [state, setState] = useState({ steps: [], progress: 0 });
  const [companyName, setCompanyName] = useState("");
  const [branch, setBranch] = useState(emptyBranch);
  const [camera, setCamera] = useState(emptyCamera);
  const [testCameraId, setTestCameraId] = useState("");
  const [detectionCameraId, setDetectionCameraId] = useState("");
  const [selectedDetections, setSelectedDetections] = useState(["shoplifting"]);
  const [schedule, setSchedule] = useState({ start: "08:00", end: "22:00", days: "mon,tue,wed,thu,fri,sat,sun" });
  const [contact, setContact] = useState(emptyContact);
  const [finalResult, setFinalResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  const notify = useCallback((message, severity = "success") => {
    setSnack({ open: true, message, severity });
  }, []);

  const scopedParams = useMemo(() => (selectedOrgId ? { organization_id: selectedOrgId } : {}), [selectedOrgId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const boot = await saasApi.getBootstrap(scopedParams);
      const orgId = selectedOrgId || boot.organization?.id || boot.scope?.organizationId || "";
      if (!selectedOrgId && orgId) setSelectedOrgId(String(orgId));
      const params = orgId ? { organization_id: orgId } : scopedParams;
      const canManageOrgs = boot.scope?.isSuperadmin;
      const [orgRows, branchRows, cameraPayload, configPayload, aiRows, contactRows, onboard] = await Promise.all([
        canManageOrgs ? saasApi.getOrganizations() : Promise.resolve([]),
        saasApi.getBranches(params),
        saasApi.getCameras(params),
        saasApi.getDetectionConfig(params),
        saasApi.getAiInstances(params),
        saasApi.getNotificationContacts(),
        saasApi.getOnboarding(params),
      ]);
      setBootstrap(boot);
      setOrganizations(orgRows);
      setBranches(branchRows);
      setCameras(cameraPayload.cameras || []);
      setConfigs(configPayload.configs || []);
      setAiInstances(Array.isArray(aiRows) ? aiRows : []);
      setContacts(Array.isArray(contactRows) ? contactRows : []);
      setState(onboard);
      if (!camera.branch_id && branchRows[0]) setCamera((current) => ({ ...current, branch_id: current.branch_id || branchRows[0].id }));
      if (!testCameraId && cameraPayload.cameras?.[0]) setTestCameraId(String(cameraPayload.cameras[0].id));
      if (!detectionCameraId && cameraPayload.cameras?.[0]) setDetectionCameraId(String(cameraPayload.cameras[0].id));
    } catch (err) {
      notify(err.message, "error");
    } finally {
      setLoading(false);
    }
  }, [camera.branch_id, detectionCameraId, notify, scopedParams, selectedOrgId, testCameraId]);

  useEffect(() => {
    load();
  }, [load]);

  const stepMap = useMemo(() => {
    return (state.steps || []).reduce((acc, step) => ({ ...acc, [step.id]: step }), {});
  }, [state.steps]);
  const percent = Math.round((state.progress || 0) * 100);
  const isSuperadmin = Boolean(bootstrap?.scope?.isSuperadmin);

  const createCompany = async () => {
    if (!companyName.trim()) return;
    setSaving(true);
    try {
      const created = await saasApi.createOrganization({ name: companyName.trim() });
      setSelectedOrgId(String(created.id));
      setCompanyName("");
      notify("Empresa creada");
      await load();
    } catch (err) {
      notify(err.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const createBranch = async () => {
    setSaving(true);
    try {
      await saasApi.createBranch({ ...branch, organization_id: selectedOrgId || undefined });
      setBranch(emptyBranch);
      notify("Sucursal creada");
      await load();
    } catch (err) {
      notify(err.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const createCamera = async () => {
    setSaving(true);
    try {
      const payload = {
        ...camera,
        display_name: camera.display_name || camera.name,
        branch_id: camera.branch_id || branches[0]?.id,
        organization_id: selectedOrgId || undefined,
      };
      await saasApi.createCamera(payload);
      setCamera({ ...emptyCamera, branch_id: payload.branch_id || "" });
      notify("Camara agregada");
      await load();
      setActiveStep(2);
    } catch (err) {
      notify(err.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setSaving(true);
    try {
      const result = await saasApi.testCameraConnection({ camera_id: Number(testCameraId), organization_id: selectedOrgId || undefined });
      notify(`${result.message}. Latencia ${result.latency_ms || 0}ms`);
      await load();
    } catch (err) {
      notify(err.message, "error");
      await load();
    } finally {
      setSaving(false);
    }
  };

  const saveDetections = async () => {
    setSaving(true);
    try {
      await Promise.all(selectedDetections.map((detection) => saasApi.saveDetectionConfig({
        camera_id: Number(detectionCameraId),
        detection_type: detection,
        enabled: true,
        organization_id: selectedOrgId || undefined,
        config_json: {
          sensitivity: detection === "shoplifting" ? 0.72 : 0.65,
          min_confidence: 0.6,
          zones_config: { zones: [], lines: [] },
        },
      })));
      notify("Detecciones configuradas");
      await load();
      setActiveStep(4);
    } catch (err) {
      notify(err.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const saveSchedule = async () => {
    setSaving(true);
    try {
      const schedule_json = {
        timezone: "America/Argentina/Buenos_Aires",
        days: schedule.days.split(",").map((day) => day.trim()).filter(Boolean),
        windows: [{ start: schedule.start, end: schedule.end }],
      };
      const instance = aiInstances[0];
      if (instance) {
        await saasApi.updateAiSchedule(instance.id, { schedule_json });
      } else {
        await saasApi.createAiInstance({
          organization_id: selectedOrgId || undefined,
          branch_id: branches[0]?.id || null,
          provider: "gcp",
          status: "not_created",
          instance_name: `ai-${selectedOrgId || "default"}`,
          schedule_json,
        });
      }
      notify("Horario IA guardado");
      await load();
      setActiveStep(5);
    } catch (err) {
      notify(err.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const saveContact = async () => {
    setSaving(true);
    try {
      await saasApi.createNotificationContact(contact);
      setContact(emptyContact);
      notify("Contacto de alerta creado");
      await load();
      setActiveStep(6);
    } catch (err) {
      notify(err.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const runFinalTest = async () => {
    setSaving(true);
    try {
      const result = await saasApi.runOnboardingFinalTest({ organization_id: selectedOrgId || undefined });
      setFinalResult(result);
      notify(result.passed ? "Prueba final aprobada" : "La prueba final tiene pendientes", result.passed ? "success" : "warning");
      await load();
    } catch (err) {
      notify(err.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const toggleDetection = (id) => {
    setSelectedDetections((current) => (
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
    ));
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box className="scene-toolbar" display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap" mb={2}>
        <Header title="Onboarding" subtitle="Alta guiada de cliente, camaras, IA, contactos y prueba final" />
        <Button variant="contained" startIcon={<RefreshIcon />} onClick={load} disabled={loading || saving}>Revisar estado</Button>
      </Box>

      <GlassCard hover={false} sx={{ mb: 3 }}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={2}>
          <Box flex={1}>
            <Typography variant="h4" mb={1}>Progreso: {percent}%</Typography>
            <LinearProgress variant="determinate" value={percent} sx={{ height: 10, borderRadius: "8px" }} />
          </Box>
          <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
            {(state.steps || []).map((step) => (
              <Chip key={step.id} label={step.done ? `${step.label}: OK` : `${step.label}: pendiente`} color={statusColor(step.done)} variant={step.done ? "filled" : "outlined"} size="small" />
            ))}
          </Stack>
        </Stack>
      </GlassCard>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={3}>
          <GlassCard hover={false}>
            <Stepper activeStep={activeStep} orientation="vertical" nonLinear>
              {stepDefinitions.map((step, index) => {
                const done = Boolean(stepMap[step.id]?.done);
                return (
                  <Step key={step.id} completed={done}>
                    <StepButton onClick={() => setActiveStep(index)}>
                      <StepLabel icon={done ? <CheckCircleOutlineIcon color="success" /> : step.icon}>
                        <Typography fontWeight={700}>{step.label}</Typography>
                        <Typography variant="caption" color={done ? "success.main" : "warning.main"}>{done ? "Completado" : "Pendiente"}</Typography>
                      </StepLabel>
                    </StepButton>
                  </Step>
                );
              })}
            </Stepper>
          </GlassCard>
        </Grid>

        <Grid item xs={12} lg={9}>
          <GlassCard hover={false}>
            {activeStep === 0 && (
              <Stack spacing={2}>
                <Typography variant="h5">Crear empresa y sucursal</Typography>
                {isSuperadmin && (
                  <>
                    <TextField select label="Empresa objetivo" value={selectedOrgId} onChange={(e) => setSelectedOrgId(e.target.value)} fullWidth>
                      {organizations.map((org) => <MenuItem key={org.id} value={org.id}>{org.name}</MenuItem>)}
                    </TextField>
                    <Stack direction={{ xs: "column", sm: "row" }} gap={1.5}>
                      <TextField label="Nueva empresa" value={companyName} onChange={(e) => setCompanyName(e.target.value)} fullWidth />
                      <Button variant="outlined" onClick={createCompany} disabled={!companyName || saving}>Crear empresa</Button>
                    </Stack>
                    <Divider />
                  </>
                )}
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}><TextField label="Nombre sucursal" value={branch.name} onChange={(e) => setBranch({ ...branch, name: e.target.value })} fullWidth /></Grid>
                  <Grid item xs={12} md={4}><TextField label="Direccion" value={branch.address} onChange={(e) => setBranch({ ...branch, address: e.target.value })} fullWidth /></Grid>
                  <Grid item xs={12} md={4}><TextField label="Ciudad" value={branch.city} onChange={(e) => setBranch({ ...branch, city: e.target.value })} fullWidth /></Grid>
                </Grid>
                <Button variant="contained" onClick={createBranch} disabled={!branch.name || saving}>Crear sucursal</Button>
              </Stack>
            )}

            {activeStep === 1 && (
              <Stack spacing={2}>
                <Typography variant="h5">Agregar camaras por RTSP o agente Windows</Typography>
                <Alert severity="info">Para agente Windows: instalalo en el cliente y dejalo iniciar sesion. Cuando reporte heartbeat, las camaras aparecen automaticamente al refrescar.</Alert>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}><TextField label="ID unico" value={camera.name} onChange={(e) => setCamera({ ...camera, name: e.target.value })} fullWidth /></Grid>
                  <Grid item xs={12} md={6}><TextField label="Nombre visible" value={camera.display_name} onChange={(e) => setCamera({ ...camera, display_name: e.target.value })} fullWidth /></Grid>
                  <Grid item xs={12} md={6}>
                    <TextField select label="Sucursal" value={camera.branch_id} onChange={(e) => setCamera({ ...camera, branch_id: e.target.value })} fullWidth>
                      {branches.map((item) => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={6}><TextField label="Zona" value={camera.zone_name} onChange={(e) => setCamera({ ...camera, zone_name: e.target.value })} fullWidth /></Grid>
                  <Grid item xs={12}><TextField label="RTSP" value={camera.rtsp_url} onChange={(e) => setCamera({ ...camera, rtsp_url: e.target.value })} fullWidth /></Grid>
                </Grid>
                <Button variant="contained" onClick={createCamera} disabled={!camera.name || !camera.rtsp_url || saving}>Agregar camara</Button>
                <Typography color="text.secondary">Camaras registradas: {cameras.length}. Agente online: {state.agent?.online || 0}/{state.agent?.total || 0}</Typography>
              </Stack>
            )}

            {activeStep === 2 && (
              <Stack spacing={2}>
                <Typography variant="h5">Probar conexion real</Typography>
                <TextField select label="Camara a probar" value={testCameraId} onChange={(e) => setTestCameraId(e.target.value)} fullWidth>
                  {cameras.map((item) => <MenuItem key={item.id} value={item.id}>{item.display_name || item.name} - {item.status}</MenuItem>)}
                </TextField>
                <Button variant="contained" startIcon={<PlayCircleOutlineIcon />} onClick={testConnection} disabled={!testCameraId || saving}>Probar conexion</Button>
                <Alert severity={stepMap.connection?.done ? "success" : "warning"} icon={stepMap.connection?.done ? <CheckCircleOutlineIcon /> : <WarningAmberOutlinedIcon />}>
                  {stepMap.connection?.done ? "Hay al menos una camara online o un agente con heartbeat reciente." : "Pendiente: ejecutar una prueba RTSP exitosa o recibir heartbeat del agente."}
                </Alert>
              </Stack>
            )}

            {activeStep === 3 && (
              <Stack spacing={2}>
                <Typography variant="h5">Elegir detecciones por camara</Typography>
                <TextField select label="Camara" value={detectionCameraId} onChange={(e) => setDetectionCameraId(e.target.value)} fullWidth>
                  {cameras.map((item) => <MenuItem key={item.id} value={item.id}>{item.display_name || item.name}</MenuItem>)}
                </TextField>
                <Grid container spacing={1}>
                  {DETECTIONS.map((detection) => (
                    <Grid item xs={12} sm={6} md={4} key={detection.id}>
                      <FormControlLabel control={<Checkbox checked={selectedDetections.includes(detection.id)} onChange={() => toggleDetection(detection.id)} />} label={detection.label} />
                    </Grid>
                  ))}
                </Grid>
                <Button variant="contained" onClick={saveDetections} disabled={!detectionCameraId || selectedDetections.length === 0 || saving}>Guardar detecciones</Button>
                <Typography color="text.secondary">Versiones configuradas: {configs.length}</Typography>
              </Stack>
            )}

            {activeStep === 4 && (
              <Stack spacing={2}>
                <Typography variant="h5">Configurar horarios IA</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}><TextField type="time" label="Inicio" InputLabelProps={{ shrink: true }} value={schedule.start} onChange={(e) => setSchedule({ ...schedule, start: e.target.value })} fullWidth /></Grid>
                  <Grid item xs={12} md={4}><TextField type="time" label="Fin" InputLabelProps={{ shrink: true }} value={schedule.end} onChange={(e) => setSchedule({ ...schedule, end: e.target.value })} fullWidth /></Grid>
                  <Grid item xs={12} md={4}><TextField label="Dias" value={schedule.days} onChange={(e) => setSchedule({ ...schedule, days: e.target.value })} fullWidth /></Grid>
                </Grid>
                <Button variant="contained" onClick={saveSchedule} disabled={saving}>Guardar horario IA</Button>
                <Typography color="text.secondary">Instancias IA: {aiInstances.length}</Typography>
              </Stack>
            )}

            {activeStep === 5 && (
              <Stack spacing={2}>
                <Typography variant="h5">Configurar contactos de alerta</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}><TextField label="Nombre" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} fullWidth /></Grid>
                  <Grid item xs={12} md={4}>
                    <TextField select label="Canal" value={contact.channel_type} onChange={(e) => setContact({ ...contact, channel_type: e.target.value })} fullWidth>
                      <MenuItem value="whatsapp">WhatsApp</MenuItem>
                      <MenuItem value="email">Email</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={4}><TextField label="Destino" value={contact.destination} onChange={(e) => setContact({ ...contact, destination: e.target.value })} fullWidth /></Grid>
                  <Grid item xs={12} md={6}><TextField type="time" label="Desde" InputLabelProps={{ shrink: true }} value={contact.start_time} onChange={(e) => setContact({ ...contact, start_time: e.target.value })} fullWidth /></Grid>
                  <Grid item xs={12} md={6}><TextField type="time" label="Hasta" InputLabelProps={{ shrink: true }} value={contact.end_time} onChange={(e) => setContact({ ...contact, end_time: e.target.value })} fullWidth /></Grid>
                </Grid>
                <Button variant="contained" onClick={saveContact} disabled={!contact.name || !contact.destination || saving}>Crear contacto</Button>
                <Typography color="text.secondary">Contactos activos: {contacts.length}</Typography>
              </Stack>
            )}

            {activeStep === 6 && (
              <Stack spacing={2}>
                <Typography variant="h5">Ejecutar prueba final</Typography>
                <Alert severity="info">La prueba valida sucursal, camara, conexion, detecciones, horario IA y contacto. No dispara mensajes reales.</Alert>
                <Button variant="contained" startIcon={<PlayCircleOutlineIcon />} onClick={runFinalTest} disabled={saving}>Ejecutar prueba final</Button>
                {(finalResult?.checks || state.steps || []).map((check) => {
                  const ok = check.ok ?? check.done;
                  const label = stepDefinitions.find((step) => step.id === check.id)?.label || check.label || check.id;
                  return <Chip key={check.id} label={`${label}: ${ok ? "OK" : "pendiente"}`} color={ok ? "success" : "warning"} sx={{ mr: 1, mb: 1 }} />;
                })}
              </Stack>
            )}
          </GlassCard>
        </Grid>
      </Grid>

      <Snackbar open={snack.open} autoHideDuration={4200} onClose={() => setSnack({ ...snack, open: false })}>
        <Alert severity={snack.severity}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Onboarding;
